import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CircularProgressRaffle } from "./circularProgress";
import { useIsMobileView } from "../../utils/responsive";
import TopPurchasers from "./topPurchasersPublic";
import FaceIcon from "@mui/icons-material/Face";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import TrafficIcon from "@mui/icons-material/Traffic";
import { RaffleActions } from "./actions";
import {
  useGetRaffleStatusQuery,
  useGetTopPurchasersQuery,
} from "../../store/api";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import {
  TrendingUp,
  BarChart3,
  DollarSign,
  Ticket,
  Users,
  BadgeCheck,
  HeartCrack,
  Star,
} from "lucide-react";
import { useEffect, useState } from "react";
import { getPrimaryColorByAccountId } from "../../utils/styleConstants";

const getSymbol = (key: string) => {
  if (["earns", "earnsUSD"].includes(key)) return "$";
  if (key === "progress") return "%";
  return "";
};

const getName = (key: string) => {
  switch (key) {
    case "progress": return "Progreso";
    case "earns": return "Ganancias";
    case "earnsUSD": return "Ganancias USD";
    case "totalTicketsSold": return "Tickets vendidos";
    case "totalPurchasers": return "Compradores";
    default: return key;
  }
};

const getIcon = (key: string) => {
  const iconSize = 24;
  const iconMap: Record<string, JSX.Element> = {
    progress: <TrendingUp size={iconSize} color="#000" />,
    earns: <BarChart3 size={iconSize} color="#000" />,
    earnsUSD: <DollarSign size={iconSize} color="#000" />,
    totalTicketsSold: <Ticket size={iconSize} color="#000" />,
    totalPurchasers: <Users size={iconSize} color="#000" />,
  };
  return iconMap[key] || null;
};
export const RaffleStatus = () => {
  const selectedRaffle = useAppSelector(({ selectedRaffle }: RootState) => selectedRaffle); 

  const { data: raffleStatus } = useGetRaffleStatusQuery(selectedRaffle.id, {
    skip: selectedRaffle.id === "default",
    refetchOnReconnect: true,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const loggedUserEmail = useAppSelector(
      ({ user }: RootState) => user.email,
  );

  useEffect(() => {
    if (raffleStatus?.progress !== undefined) {
      setProgressInput(raffleStatus?.progress?.toString());
    }
  }, [raffleStatus?.progress]);
  
  const [progressInput, setProgressInput] = useState<string>("0");

  const { data: topPurchasers } = useGetTopPurchasersQuery(selectedRaffle.id, {
    skip: selectedRaffle.id === "default",
    refetchOnReconnect: true,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const showData = selectedRaffle.id !== "default";

  return (
    <Box>
      {raffleStatus && (
        <Grid container spacing={2} mb={3}>
          {Object.entries(raffleStatus).map(([key, value]) => (
            <Grid item xs={6} sm={6} md={4} key={key}>
              <Paper
                elevation={4}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,

                }}
              >
                <Avatar
                  sx={{
                    bgcolor: getPrimaryColorByAccountId(selectedRaffle.accountId),
                    width: 48,
                    height: 48,
                  }}
                  variant="rounded"
                >
                  {getIcon(key)}
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {getName(key)}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {key === "earnsUSD" && selectedRaffle.id === "a123f062-809b-4a51-a723-d9076fffa3a0"
                      ? value / 2
                      : value}
                    {getSymbol(key)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={6} sm={6} md={4} key={'1'}>
              <Paper
                elevation={4}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,

                }}
              >
                <Avatar
                  sx={{
                    bgcolor: getPrimaryColorByAccountId(selectedRaffle.accountId),
                    width: 48,
                    height: 48,
                  }}
                  variant="rounded"
                >
                  <Star color="#000"></Star>
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Rifa pro
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedRaffle.isPremium ? <BadgeCheck color="blue"></BadgeCheck> : <HeartCrack color="red"></HeartCrack>}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
        </Grid>
      )}

      {showData ? (
        <Card elevation={3}>
          <CardHeader
            title={
              <Typography variant="h6" fontWeight="bold">
                {selectedRaffle.name}
                <Chip
                  label={selectedRaffle.status === "ACTIVE" ? "Activa" : "Inactiva"}
                  color={selectedRaffle.status === "ACTIVE" ? "success" : "default"}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Typography>
            }
            subheader={`Límite: ${selectedRaffle.limit} tickets • $${selectedRaffle.ticketPriceUSD} USD c/u`}
          />
          <CardContent sx={{ background: 'transparent', height: '100%', px: 0 }}>
            <Grid container spacing={3} height={"auto"} p={5}>
              <Grid item xs={12} md={6} height={"auto"} display={"flex"} justifyContent={"center"} alignItems={"center"} pr={2}>
                <CircularProgressRaffle
                  value={
                    Number.parseFloat(progressInput)
                  }
                  size={160}
                />
              </Grid>
              <Grid item xs={12} md={6} pr={2}>
                <Box px={2} mb={4}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="textSecondary">
                            Progreso
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {Number.parseFloat(progressInput)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Number.parseFloat(progressInput)}
                        sx={{ height: 10, borderRadius: 5 }}
                    />
                </Box>
              </Grid>
              <Grid item xs={12} md={6} pr={2}>
                <TopPurchasers raffleId={selectedRaffle.id} />
              </Grid>
              <Grid item xs={12}>
                <RaffleActions
                  isActiveRaffle={selectedRaffle.status === "ACTIVE"}
                  raffleId={selectedRaffle.id}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="body1" textAlign="center" color="text.secondary">
              Selecciona una rifa del listado para comenzar.
            </Typography>
          </CardContent>
        </Card>
      )}

      {
        (loggedUserEmail === 'oscaralarcon@gmail.com' || loggedUserEmail === 'alejandroelgochito@gmail.com') && <Box>
        <TextField
            type="number"
            size="small"
            label="Progreso (%)"
            value={progressInput}
            onChange={(e) => {
              const newValue = e.target.value;
              // Permitir decimales válidos como "0", "0.", "0.5", "100", etc.
              if (/^\d{0,3}(\.\d{0,2})?$/.test(newValue)) {
                setProgressInput(newValue);
              }
            }}
            onBlur={() => {
              const numericValue = parseFloat(progressInput);
              if (!isNaN(numericValue)) {
                setProgressInput(Math.max(0, Math.min(100, numericValue)).toString());
              } else {
                setProgressInput("0");
              }
            }}
            sx={{ mt: 2, width: "120px" }}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
          />

        </Box>
      }
    </Box>
  );
};

export default RaffleStatus;
